// src/components/images/ImagePreviewCarousel.jsx
"use client";

import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const THUMB = 64; // px — strip height and square thumb size

// Lazy loader for HEIC -> JPEG converter (heic2any). If CDN fails, we'll just show a badge.
async function ensureHeic2Any() {
  if (typeof window === "undefined") return null;
  if (window.heic2any) return window.heic2any;
  // Load once per session
  await new Promise((resolve, reject) => {
    const el = document.createElement("script");
    el.src = "https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js";
    el.async = true;
    el.onload = resolve;
    el.onerror = reject;
    document.head.appendChild(el);
  }).catch(() => {});
  return window.heic2any || null;
}

function isHeicFile(f) {
  const name = (f?.name || "").toLowerCase();
  const type = (f?.type || "").toLowerCase();
  return name.endsWith(".heic") || name.endsWith(".heif") || type.includes("heic") || type.includes("heif");
}

export default function ImagePreviewCarousel({ files = [], onRemove }) {
  // We produce an array equal in length to files:
  // { url: string|null, failed: boolean, label: string }
  const [thumbs, setThumbs] = useState([]);

  // Create (or convert) preview blobs
  useEffect(() => {
    let cancelled = false;
    let createdUrls = [];

    async function makeThumbs() {
      const out = [];

      // Try to load heic2any only if we detect HEIC
      const hasHeic = files.some((f) => isHeicFile(f));
      const heic2any = hasHeic ? await ensureHeic2Any() : null;

      for (const f of files) {
        const label = (f.name || "image").split(".").pop()?.toUpperCase() || "IMG";

        // HEIC/HEIF — try to convert to JPEG
        if (isHeicFile(f) && heic2any) {
          try {
            const blob = await heic2any({ blob: f, toType: "image/jpeg", quality: 0.92 });
            const url = URL.createObjectURL(blob);
            createdUrls.push(url);
            out.push({ url, failed: false, label: "JPG" });
            continue;
          } catch {
            // fall through to failed badge
          }
        }

        // For standard web formats, just show the blob directly
        if (/^image\//i.test(f.type) && !isHeicFile(f)) {
          try {
            const url = URL.createObjectURL(f);
            createdUrls.push(url);
            out.push({ url, failed: false, label });
            continue;
          } catch {
            // fall through to failed badge
          }
        }

        // If we got here, preview failed
        out.push({ url: null, failed: true, label });
      }

      if (!cancelled) setThumbs(out);
    }

    makeThumbs();

    // Cleanup blob URLs on change/unmount
    return () => {
      cancelled = true;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
      createdUrls = [];
    };
  }, [files]);

  // Simple key to re-render when files change order/length
  const listKey = useMemo(() => files.map((f) => f.name + f.size + f.lastModified).join("|"), [files]);

  return (
    <div className="relative" style={{ maxHeight: THUMB }}>
      <div
        key={listKey}
        className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1"
        style={{ height: THUMB }}
        aria-label="Selected images"
      >
        {thumbs.map((t, i) => (
          <div
            key={i}
            className="relative flex-shrink-0 rounded-md overflow-hidden border border-[var(--border)] bg-[var(--surface-2)]"
            style={{ width: THUMB, height: THUMB }}
          >
            {t.url && !t.failed ? (
              <img
                src={t.url}
                alt={`preview-${i}`}
                className="w-full h-full object-cover"
                draggable={false}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[10px] text-[var(--muted)]">
                {t.label}
              </div>
            )}

            <button
              type="button"
              onClick={() => onRemove?.(i)}
              className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1"
              title="Remove"
              aria-label={`Remove image ${i + 1}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
